import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import usePbTasksApi from './config';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import { useAuth } from '../../context/AuthContext';

const Container = styled.div``;

export default function PbTasksPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const location = useLocation();
  const writable = canWrite ? canWrite('PB') : false;

  // Deep-link support: the preboarding list's "tasks" action navigates here
  // with ?preboardingId=<guid> to open the page pre-filtered to one hire.
  const [queryParams, setQueryParams] = useState(() => {
    const preboardingId = new URLSearchParams(location.search).get('preboardingId');
    const base = { pageSize: 25, pageNumber: 1 };
    return preboardingId ? { ...base, 'preboardingId[eq]': preboardingId } : base;
  });
  const [filterList, setFilterList] = useState([]);
  const [confirm, setConfirm] = useState(null);

  // drawer: { mode: 'create' } | { mode: 'edit', initialValues }
  const [drawer, setDrawer] = useState(null);

  const {
    fetching, data, pagination, columns: baseColumns, createSchema, editSchema,
    create, update, start, complete, approve, reject, cancel,
  } = usePbTasksApi(queryParams);

  const handleNewButtonClick = useCallback(() => setDrawer({ mode: 'create' }), []);

  const handleSave = useCallback(async (formData) => {
    const success = drawer?.mode === 'edit'
      ? await update(drawer.initialValues.id, formData)
      : await create(formData);
    if (success) setDrawer(null);
    return success;
  }, [drawer, create, update]);

  const handleDataStateChange = useCallback((act, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => setFilterList(newFilterList), []);

  const buildActions = useCallback((row) => {
    if (!row || !writable) return [];
    const actions = [];
    if (row.status !== 'completed' && row.status !== 'cancelled') {
      actions.push({
        type: 'edit',
        label: t('common.edit'),
        onClick: () => setDrawer({
          mode: 'edit',
          initialValues: {
            id: row.id,
            slaDueAt: row.slaDueAt || '',
            notesMarkdown: row.notesMarkdown || '',
          },
        }),
      });
    }
    if (row.status === 'assigned') {
      actions.push({
        type: 'resume',
        label: t('preboardingTask.action.start'),
        onClick: () => setConfirm({
          message: t('preboardingTask.confirm.start', { title: row.title }),
          onYes: () => start(row.id),
        }),
      });
    }
    if (row.status === 'assigned' || row.status === 'in_progress') {
      actions.push({
        type: 'approve',
        label: t('preboardingTask.action.complete'),
        onClick: () => setConfirm({
          message: t('preboardingTask.confirm.complete', { title: row.title }),
          onYes: () => complete(row.id),
        }),
      });
    }
    if (row.status === 'hr_approval_pending') {
      actions.push({
        type: 'accept',
        label: t('preboardingTask.action.approve'),
        onClick: () => setConfirm({
          message: t('preboardingTask.confirm.approve', { title: row.title }),
          onYes: () => approve(row.id),
        }),
      });
      actions.push({
        type: 'reject',
        label: t('preboardingTask.action.reject'),
        onClick: () => setConfirm({
          message: t('preboardingTask.confirm.reject', { title: row.title }),
          onYes: () => reject(row.id, t('preboardingTask.defaultRejectNote')),
        }),
      });
    }
    if (row.status !== 'completed' && row.status !== 'cancelled') {
      actions.push({
        type: 'cancel',
        label: t('preboardingTask.action.cancel'),
        onClick: () => setConfirm({
          message: t('preboardingTask.confirm.cancel', { title: row.title }),
          onYes: () => cancel(row.id),
        }),
      });
    }
    return actions;
  }, [t, writable, start, complete, approve, reject, cancel]);

  const drawerSchema = drawer?.mode === 'edit' ? editSchema : createSchema;

  const columns = useMemo(() => {
    if (!writable) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions', label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            const actions = buildActions(row);
            return actions.length > 0 ? <OptionsMenu actions={actions} row={row} /> : null;
          },
        },
      },
    ];
  }, [baseColumns, data, buildActions, writable]);

  return (
    <Container>
      <GenericFormDrawer
        open={!!drawer}
        onClose={() => setDrawer(null)}
        title={drawer?.mode === 'edit' ? t('preboardingTask.edit') : t('preboardingTask.new')}
        schema={drawerSchema}
        initialValues={drawer?.mode === 'edit' ? drawer.initialValues : null}
        onSave={handleSave}
      />

      {useMemo(() => (
        <DataTable
          title={t('preboardingTask.pageTitle')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={writable}
          onNewButtonClick={handleNewButtonClick}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, columns, data, pagination, writable, handleNewButtonClick,
        filterList, handleFilterConfirm, handleDataStateChange,
      ])}
      <ConfirmDialog
        isOpen={!!confirm}
        message={confirm?.message}
        onConfirm={async () => { await confirm.onYes(); setConfirm(null); }}
        onCancel={() => setConfirm(null)}
      />
    </Container>
  );
}
